import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import styles from "./select-meter.module.scss";
import { ExpandIcon, CheckIcon, CloseIcon } from "../../../../assets/icons";
import useWindowDimensions from "../../../../hooks/use-window-dimensions";

export interface MeterOption {
	id: string;
	name: string;
	meterNumber: string;
	type?: string;
}

interface SelectMeterProps {
	meters: MeterOption[];
	selectedId?: string;
	placeholder?: string;
	disabled?: boolean;
	onSelect: (meter: MeterOption) => void;
}

const MENU_GAP = 6; // px between trigger and menu
const MENU_MAX_HEIGHT = 300;

// "Light Nduka" -> "LN"; a single word uses its first two letters ("Light" -> "LI").
const getInitials = (name: string): string => {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "?";
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return (parts[0][0] + parts[1][0]).toUpperCase();
};

const SelectMeter: React.FC<SelectMeterProps> = ({ meters, selectedId, placeholder, disabled = false, onSelect }) => {
	const { t } = useTranslation();
	const placeholderText = placeholder ?? t("common.web.selectMeter");
	const [open, setOpen] = useState(false);
	const { isMobile } = useWindowDimensions();
	const containerRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLUListElement>(null);
	const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

	const selected = meters.find(m => m.id === selectedId);

	// Position the desktop popover relative to the trigger, flipping up when short on space below.
	const updatePosition = useCallback(() => {
		const el = triggerRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		const spaceBelow = window.innerHeight - rect.bottom;
		const openUp = spaceBelow < MENU_MAX_HEIGHT && rect.top > spaceBelow;
		if (openUp) {
			setMenuStyle({
				position: "fixed",
				left: rect.left,
				width: rect.width,
				bottom: window.innerHeight - rect.top + MENU_GAP,
				maxHeight: Math.min(MENU_MAX_HEIGHT, Math.max(0, rect.top - MENU_GAP - 8)),
			});
		} else {
			setMenuStyle({
				position: "fixed",
				left: rect.left,
				width: rect.width,
				top: rect.bottom + MENU_GAP,
				maxHeight: Math.min(MENU_MAX_HEIGHT, Math.max(0, spaceBelow - MENU_GAP - 8)),
			});
		}
	}, []);

	useLayoutEffect(() => {
		if (open && !isMobile) updatePosition();
	}, [open, isMobile, updatePosition]);

	// Desktop: keep the popover anchored while scrolling/resizing.
	useEffect(() => {
		if (!open || isMobile) return;
		const handle = () => updatePosition();
		window.addEventListener("scroll", handle, true);
		window.addEventListener("resize", handle);
		return () => {
			window.removeEventListener("scroll", handle, true);
			window.removeEventListener("resize", handle);
		};
	}, [open, isMobile, updatePosition]);

	// Desktop: close when clicking outside both the trigger and the (portaled) menu.
	useEffect(() => {
		if (!open || isMobile) return;
		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as Node;
			if (containerRef.current?.contains(target)) return;
			if (menuRef.current?.contains(target)) return;
			setOpen(false);
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [open, isMobile]);

	// Mobile: lock background scroll while the bottom sheet is open.
	useEffect(() => {
		if (!open || !isMobile) return;
		const previous = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = previous;
		};
	}, [open, isMobile]);

	const handleSelect = (meter: MeterOption) => {
		onSelect(meter);
		setOpen(false);
	};

	const meterRow = (meter: MeterOption) => (
		<>
			<span className={styles.avatar}>{getInitials(meter.name)}</span>
			<span className={styles.info}>
				<span className={styles.nameRow}>
					<span className={styles.name}>{meter.name}</span>
					{meter.type && <span className={styles.typeBadge}>{meter.type}</span>}
				</span>
				<span className={styles.number}>{meter.meterNumber}</span>
			</span>
		</>
	);

	const popover = createPortal(
		<ul className={styles.menu} role="listbox" ref={menuRef} style={menuStyle}>
			{meters.length === 0 ? (
				<li className={styles.empty}>{t("common.web.noMetersAvailable")}</li>
			) : (
				meters.map(meter => (
					<li key={meter.id} role="option" aria-selected={meter.id === selectedId}>
						<button type="button" className={styles.option} onClick={() => handleSelect(meter)}>
							<span className={styles.optionMain}>{meterRow(meter)}</span>
							{meter.id === selectedId && <CheckIcon className={styles.check} />}
						</button>
					</li>
				))
			)}
		</ul>,
		document.body
	);

	const bottomSheet = createPortal(
		<div className={styles.sheetOverlay} onMouseDown={() => setOpen(false)}>
			<div className={styles.sheet} role="dialog" aria-modal="true" onMouseDown={e => e.stopPropagation()}>
				<div className={styles.sheetHeader}>
					<button type="button" className={styles.sheetClose} onClick={() => setOpen(false)} aria-label={t("account.web.close")}>
						<CloseIcon />
					</button>
					<span className={styles.sheetTitle}>{placeholderText}</span>
				</div>
				<div className={styles.sheetDivider} />
				<ul className={styles.sheetList} role="listbox">
					{meters.length === 0 ? (
						<li className={styles.empty}>{t("common.web.noMetersAvailable")}</li>
					) : (
						meters.map(meter => (
							<li key={meter.id} role="option" aria-selected={meter.id === selectedId}>
								<button type="button" className={styles.sheetOption} onClick={() => handleSelect(meter)}>
									<span className={styles.optionMain}>{meterRow(meter)}</span>
									{meter.id === selectedId && <CheckIcon className={styles.check} />}
								</button>
							</li>
						))
					)}
				</ul>
			</div>
		</div>,
		document.body
	);

	return (
		<div className={styles.container} ref={containerRef}>
			<button
				type="button"
				ref={triggerRef}
				disabled={disabled}
				className={styles.trigger}
				onClick={() => !disabled && setOpen(o => !o)}
			>
				{selected ? (
					<span className={styles.optionMain}>{meterRow(selected)}</span>
				) : (
					<span className={styles.placeholder}>{placeholderText}</span>
				)}
				<ExpandIcon className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`} />
			</button>

			{open && (isMobile ? bottomSheet : popover)}
		</div>
	);
};

export default SelectMeter;
